import { html, nothing } from "lit";
import type { AgentQueueStatus, AgentRunEntry } from "../types.ts";
import { formatAgo } from "../format.ts";

export type AgentsSettingsProps = {
  queueStatus: AgentQueueStatus | null;
  queueLoading: boolean;
  queueError: string | null;
  concurrencyValue: number;
  concurrencyDirty: boolean;
  concurrencySaving: boolean;
  onConcurrencyChange: (value: number) => void;
  onConcurrencySave: () => void;
  onQueueRefresh: () => void;
};

function renderRunCard(entry: AgentRunEntry) {
  const statusIcon =
    entry.status === "running"
      ? html`
          <span class="queue-status-icon spinning">⟳</span>
        `
      : entry.status === "done"
        ? html`
            <span class="queue-status-icon ok">✓</span>
          `
        : html`
            <span class="queue-status-icon error">✗</span>
          `;

  const timeLabel = entry.endedAt
    ? formatAgo(entry.endedAt)
    : entry.startedAt
      ? `started ${formatAgo(entry.startedAt)}`
      : `created ${formatAgo(entry.createdAt)}`;

  return html`
    <div class="queue-card ${entry.status === "running" ? "queue-card--active" : ""}">
      <div class="queue-card-header">
        ${statusIcon}
        <span class="queue-card-agent mono">${entry.agentId}</span>
        ${entry.label ? html`<span class="queue-card-label">${entry.label}</span>` : nothing}
      </div>
      <div class="queue-card-task">${entry.task.length > 120 ? entry.task.slice(0, 120) + "…" : entry.task}</div>
      <div class="queue-card-meta">
        <span class="muted">${timeLabel}</span>
        ${entry.error ? html`<span class="queue-card-error">${entry.error}</span>` : nothing}
      </div>
    </div>
  `;
}

function renderEmptyState(message: string) {
  return html`
    <div class="queue-empty muted">${message}</div>
  `;
}

export function renderAgentsSettingsPanel(props: AgentsSettingsProps) {
  const {
    queueStatus,
    queueLoading,
    queueError,
    concurrencyValue,
    concurrencyDirty,
    concurrencySaving,
    onConcurrencyChange,
    onConcurrencySave,
    onQueueRefresh,
  } = props;

  const maxConcurrent = queueStatus?.maxConcurrent ?? concurrencyValue;
  const licenseLimit = queueStatus?.licenseLimit ?? 64;
  const active = queueStatus?.active ?? [];
  const completed = queueStatus?.completed ?? [];
  const atLicenseLimit = concurrencyValue >= licenseLimit;

  return html`
    <div class="agents-settings-panel">
      <section class="card">
        <div class="card-title">Agent Concurrency</div>
        <div class="card-sub">Maximum agents that can run simultaneously.</div>

        <div class="concurrency-control" style="margin-top: 20px;">
          <div class="row" style="align-items: center; gap: 16px; flex-wrap: wrap;">
            <label class="field" style="flex: 1; min-width: 200px;">
              <span>
                Max Concurrent Agents
                ${
                  atLicenseLimit
                    ? html`
                        <span class="chip chip-warn" style="margin-left: 8px">License Maximum</span>
                      `
                    : nothing
                }
              </span>
              <div class="row" style="align-items: center; gap: 12px; margin-top: 8px;">
                <input
                  type="range"
                  min="1"
                  max="${licenseLimit}"
                  .value=${String(concurrencyValue)}
                  @input=${(e: Event) => onConcurrencyChange(Number((e.target as HTMLInputElement).value))}
                  style="flex: 1;"
                />
                <span class="concurrency-value mono">${concurrencyValue}</span>
              </div>
            </label>
            <button
              class="btn primary"
              type="button"
              ?disabled=${!concurrencyDirty || concurrencySaving}
              @click=${onConcurrencySave}
            >
              ${concurrencySaving ? "Saving…" : "Save"}
            </button>
          </div>
          <div class="muted" style="font-size: 12px; margin-top: 8px;">
            License limit: ${licenseLimit}. Changes persist to config.
          </div>
        </div>
      </section>

      <section class="card" style="margin-top: 16px;">
        <div class="row" style="justify-content: space-between; align-items: center;">
          <div>
            <div class="card-title">Agent Queue</div>
            <div class="card-sub">Real-time view of agent execution status.</div>
          </div>
          <div class="row" style="gap: 8px;">
            <button
              class="btn btn--sm"
              type="button"
              ?disabled=${queueLoading}
              @click=${onQueueRefresh}
            >
              ${queueLoading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>

        ${queueError ? html`<div class="callout danger" style="margin-top: 12px;">${queueError}</div>` : nothing}

        <div class="queue-sections" style="margin-top: 20px;">
          <div class="queue-section">
            <div class="queue-section-header">
              <span class="queue-section-title">In Progress</span>
              <span class="queue-badge ${active.length > 0 ? "queue-badge--active" : ""}">${active.length}</span>
            </div>
            ${
              active.length === 0
                ? renderEmptyState("No agents running.")
                : html`<div class="queue-cards">${active.map(renderRunCard)}</div>`
            }
          </div>

          <div class="queue-section" style="margin-top: 24px;">
            <div class="queue-section-header">
              <span class="queue-section-title">Recently Completed</span>
              <span class="queue-badge">${completed.length}</span>
            </div>
            ${
              completed.length === 0
                ? renderEmptyState("No completed agents yet.")
                : html`<div class="queue-cards">${completed.map(renderRunCard)}</div>`
            }
          </div>
        </div>
      </section>
    </div>
  `;
}
